import { jsx as _jsx } from "react/jsx-runtime";
import * as XLSX from "xlsx";
import { PButton } from "@ui/components/buttons/PButton";
import { toast } from "react-toastify";
import { ExcelIcon } from "@ui/icons/CustomIcons";
import { PIconButton } from "@ui/components/buttons/PIconButton";
import { useMediaQuery, useTheme } from "@mui/material";
import { ExportToExcelAsync } from "@application/features/invitation/invitationThunk";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { useEffect } from "react";
import { GetAllPickupLocationsAsync } from "@application/features/pickupLocation/pickupLoctionThunk";
export const ExportToExcel = ({ fileName, eventType, selectedParams, exportType }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery("(max-width:650px)");
    const isMd = useMediaQuery(theme.breakpoints.down("lg"));
    const { pickupLocations } = useAppSelector((state) => state.pickupLocation);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(GetAllPickupLocationsAsync());
    }, []);
    const getStatusLabel = (status) => {
        switch (status) {
            case 1:
                return "Pending";
            case 2:
                return "In Progress";
            case 3:
                return "Registered";
            case 4:
                return "Invited";
            case 5:
                return "Expired";
            case 6:
                return "Declined";
            default:
                return "Unknown";
        }
    };
    const transformData = (items) => {
        //#region Joining dates
        // 1. Find every unique joining date string (ISO or however you store it)
        const allJoiningDates = Array.from(new Set(items.flatMap((item) => Array.isArray(item.joiningDates) ? item.joiningDates : [])))
            // sort chronologically
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        // 2. Build one-hot fields for each date
        const joinDateFields = {};
        allJoiningDates.forEach((dateStr) => {
            // e.g. "Joining Date June 1"
            const label = `Aanmeldingsdatum ${new Date(dateStr).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
            })}`;
            joinDateFields[label] = (item) => Array.isArray(item.joiningDates) && item.joiningDates.includes(dateStr)
                ? 1
                : 0;
        });
        //#endregion joining dates
        //#region Parking dates
        // 1. collect every unique parking-date string
        const allParkingDates = Array.from(new Set(items.flatMap((item) => Array.isArray(item.transportInfo?.transportParkingDates)
            ? item.transportInfo.transportParkingDates.map((d) => d.date)
            : []))).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        // 2. build one-hot fields
        const parkingDateFields = {};
        allParkingDates.forEach((dateStr) => {
            const label = `Parkeren ${new Date(dateStr).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
            })}`;
            parkingDateFields[label] = (item) => Array.isArray(item.transportInfo?.transportParkingDates) &&
                item.transportInfo.transportParkingDates.some((d) => d.date === dateStr)
                ? 1
                : 0;
        });
        //#endregion
        //#region Accommodation dates
        const allAccommodationDates = Array.from(new Set(items.flatMap((item) => Array.isArray(item.accommodationInfo?.accommodationDates)
            ? item.accommodationInfo.accommodationDates.map((d) => d.date)
            : []))).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const accommodationDateFields = {};
        allAccommodationDates.forEach((dateStr) => {
            const label = `Accomodatie ${new Date(dateStr).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
            })}`;
            accommodationDateFields[label] = (item) => Array.isArray(item.accommodationInfo?.accommodationDates) &&
                item.accommodationInfo.accommodationDates.some((d) => d.date === dateStr)
                ? 1
                : 0;
        });
        //#endregion
        const commonFields = {
            Id: (item) => item.uniqueId,
            Voornaam: "firstName",
            "Tussenvoegsel (optioneel)": "middleName",
            Achternaam: "lastName",
            "E-mailadres": "userEmail",
            "Bedrijf": "companyName",
            Adres: "address",
            Telefoonnummer: "mobileNumber",
            Status: "invitationStatusAsString",
            Uitnodigingsdatum: "invitedDate",
            ...joinDateFields,
            // "Joining Dates": (item: any) =>
            //   Array.isArray(item.joiningDates) ? item.joiningDates.join(", ") : "N/A",
            Link: (item) => item.magicLink || "N/A",
            // "Limited Mobility": (item: any) =>
            //   item.importantInfo?.isDisability ? "Yes" : "No",
            // "Mobility Comments": (item: any) =>
            //   item.importantInfo?.disabilityComments,
            // "Dietary Comments": (item: any) =>
            //   item.importantInfo?.dietaryRequirements,
            // "Additional Comments": (item: any) => item.importantInfo?.comments,
        };
        const familyFields = {
            Geboortedatum: (item) => item?.dateOfBirth
                ? new Date(item.dateOfBirth).toLocaleDateString()
                : "",
            Leeftijd: (item) => item?.dateOfBirth
                ? Math.floor((new Date().getTime() - new Date(item?.dateOfBirth).getTime()) /
                    (1000 * 60 * 60 * 24 * 365.25))
                : "",
            Gezelschap: "comesWith",
            Geregistreerd: (item) => (item.status == 3 ? 1 : 0),
            "Totaal aantal gezelschap": "totalPersonInvited",
            "Geregistreerd + aantal gezelschap": (item) => (item.status === 3 ? 1 : 0) + (item.totalPersonInvited || 0),
            Vervoer: (item) => item.transportInfo?.transportOption === 1
                ? "Bus"
                : item.transportInfo?.transportOption === 2
                    ? "Car"
                    : "Other",
            "Aantal personen": (item) => item.transportInfo?.noOfPeople ? item.transportInfo?.noOfPeople : 0,
            "Aantal auto's": (item) => item.transportInfo?.noOfCars ? item.transportInfo?.noOfCars : 0,
            Opstaplocatie: (item) => {
                const location = pickupLocations?.find((x) => x.id === item.transportInfo?.pickupLocationId);
                return location && item.transportInfo?.transportOption === 1
                    ? `${location.businessOrBoardingLocation}, ${location.postalCode}, ${location.city}`
                    : "";
            },
            Vervoersopmerkingen: (item) => item.transportInfo?.transportComments
                ? item.transportInfo?.transportComments
                : "",
            "Beperkte mobiliteit": (item) => item.importantInfo?.isDisability ? "Yes" : "No",
            "Opmerkingen over mobiliteit": (item) => item.importantInfo?.disabilityComments,
            Dieetwensen: (item) => item.importantInfo?.dietaryRequirements,
            "Aanvullende opmerkingen": (item) => item.importantInfo?.comments,
            "Registratie-/wijzigingsdatum": (item) => new Date(item.lastModifiedDate).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
            }),
        };
        // const internalDirectorsFields = {
        //   Bedrijf: "companyName",
        //   Address: "address",
        //   "Mobile Number": "mobileNumber",
        //   "Shirt Model": "gender",
        //   "Shirt Size": "shirtSize",
        //   Accommodation: (item: any) =>
        //     item.accommodationInfo && item.accommodationInfo.stayOverNight
        //       ? "Yes"
        //       : "No",
        //   ...accommodationDateFields,
        //   Parking: (item: any) =>
        //     item.transportInfo && item.transportInfo.isParkingRequired
        //       ? "Yes"
        //       : "No",
        //   ...parkingDateFields,
        //   "Parking Reason": (item: any) => item.transportInfo?.transportComments,
        //   "Limited Mobility": (item: any) =>
        //     item.importantInfo?.isDisability ? "Yes" : "No",
        //   "Mobility Comments": (item: any) =>
        //     item.importantInfo?.disabilityComments,
        //   "Dietary Comments": (item: any) =>
        //     item.importantInfo?.dietaryRequirements,
        //   "Additional Comments": (item: any) => item.importantInfo?.comments,
        //   "Total Slots": "noOfInvites",
        //   "External Link": (item: any) => item.externalFormLink,
        //   "Registered/Modified Date": (item: any) =>
        //     new Date(item.lastModifiedDate).toLocaleDateString("en-US", {
        //       month: "long",
        //       day: "numeric",
        //     }),
        // };
        // const internalDirectorsFields = {
        //   Type : (item:any)=>item.invitationTypeAsString,
        //   Adres: "address",
        //   "Mobiel nummer": "mobileNumber",
        //   Shirtmodel: "gender",
        //   Shirtmaat: "shirtSize",
        //   Overnachting: (item: any) =>
        //     item.accommodationInfo && item.accommodationInfo.stayOverNight
        //       ? "Yes"
        //       : "No",
        //   ...accommodationDateFields,
        //   Parkeren: (item: any) =>
        //     item.transportInfo && item.transportInfo.isParkingRequired
        //       ? "Yes"
        //       : "No",
        //   ...parkingDateFields,
        //   "Reden voor parkeren": (item: any) =>
        //     item.transportInfo?.transportComments,
        //   "Beperkte mobiliteit": (item: any) =>
        //     item.importantInfo?.isDisability ? "Yes" : "No",
        //   "Opmerkingen over mobiliteit": (item: any) =>
        //     item.importantInfo?.disabilityComments,
        //   Dieetwensen: (item: any) => item.importantInfo?.dietaryRequirements,
        //   "Aanvullende opmerkingen": (item: any) => item.importantInfo?.comments,
        //   "Relaties geboekte plekken": "noOfInvites",
        //   "Externe link": (item: any) => item.externalFormLink,
        //   "Registratie-/wijzigingsdatum": (item: any) =>
        //     new Date(item.lastModifiedDate).toLocaleDateString("en-US", {
        //       month: "long",
        //       day: "numeric",
        //     }),
        // };
        // const externalDirectorsFields = {
        //   invitationTypeAsString : (item:any)=>item.invitationTypeAsString,
        //   Adres: "address",
        //   "Mobiel nummer": "mobileNumber",
        //   Shirtmodel: "gender",
        //   Shirtmaat: "shirtSize",
        //   Overnachting: (item: any) =>
        //     item.accommodationInfo && item.accommodationInfo.stayOverNight
        //       ? "Yes"
        //       : "No",
        //   ...accommodationDateFields,
        //   Parkeren: (item: any) =>
        //     item.transportInfo && item.transportInfo.isParkingRequired
        //       ? "Yes"
        //       : "No",
        //   ...parkingDateFields,
        //   "Reden voor parkeren": (item: any) =>
        //     item.transportInfo?.transportComments,
        //   "Beperkte mobiliteit": (item: any) =>
        //     item.importantInfo?.isDisability ? "Yes" : "No",
        //   "Opmerkingen over mobiliteit": (item: any) =>
        //     item.importantInfo?.disabilityComments,
        //   Dieetwensen: (item: any) => item.importantInfo?.dietaryRequirements,
        //   "Aanvullende opmerkingen": (item: any) => item.importantInfo?.comments,
        //   "Uitgenodigd door": (item: any) =>
        //     item.externalInfoDto?.directorFullName || "",
        //   "Bedrijf van directeur": (item: any) => item.companyName || "",
        //   "Registratie-/wijzigingsdatum": (item: any) =>
        //     new Date(item.lastModifiedDate).toLocaleDateString("en-US", {
        //       month: "long",
        //       day: "numeric",
        //     }),
        // };
        // const externalDirectorsFields = {
        //   Company: (item: any) => item.externalInfoDto?.companyName,
        //   Address: "address",
        //   "Mobile Number": "mobileNumber",
        //   "Shirt Model": "gender",
        //   "Shirt Size": "shirtSize",
        //   Accommodation: (item: any) =>
        //     item.accommodationInfo && item.accommodationInfo.stayOverNight
        //       ? "Yes"
        //       : "No",
        //   ...accommodationDateFields,
        //   Parking: (item: any) =>
        //     item.transportInfo && item.transportInfo.isParkingRequired
        //       ? "Yes"
        //       : "No",
        //   ...parkingDateFields,
        //   "Parking Reason": (item: any) => item.transportInfo?.transportComments,
        //   "Limited Mobility": (item: any) =>
        //     item.importantInfo?.isDisability ? "Yes" : "No",
        //   "Mobility Comments": (item: any) =>
        //     item.importantInfo?.disabilityComments,
        //   "Dietary Comments": (item: any) =>
        //     item.importantInfo?.dietaryRequirements,
        //   "Additional Comments": (item: any) => item.importantInfo?.comments,
        //   "Invited By": (item: any) => item.externalInfoDto?.directorFullName || "",
        //   "Director's Company": (item: any) => item.companyName || "",
        //   "Registered/Modified Date": (item: any) =>
        //     new Date(item.lastModifiedDate).toLocaleDateString("en-US", {
        //       month: "long",
        //       day: "numeric",
        //     }),
        // };
        const getDirectorsFields = () => {
            return {
                Type: (item) => item.invitationTypeAsString,
                Bedrijf: (item) => item.invitationTypeAsString === "extern"
                    ? item.externalInfoDto?.companyName
                    : item.companyName,
                "Uitgenodigd door": (item) => item.invitationTypeAsString === "extern"
                    ? item.externalInfoDto?.directorFullName
                    : "",
                "Externe link": (item) => item.invitationTypeAsString === "intern" ? item.externalFormLink : "",
                "Bedrijf van directeur": (item) => item.invitationTypeAsString === "extern" ? item.companyName : "",
                Adres: "address",
                "Mobiel nummer": "mobileNumber",
                Shirtmodel: "gender",
                Shirtmaat: "shirtSize",
                Overnachting: (item) => item.accommodationInfo?.stayOverNight ? "Yes" : "No",
                ...accommodationDateFields,
                Parkeren: (item) => item.transportInfo?.isParkingRequired ? "Yes" : "No",
                ...parkingDateFields,
                "Reden voor parkeren": (item) => item.transportInfo?.transportComments || "",
                "Beperkte mobiliteit": (item) => item.importantInfo?.isDisability ? "Yes" : "No",
                "Opmerkingen over mobiliteit": (item) => item.importantInfo?.disabilityComments || "",
                Dieetwensen: (item) => item.importantInfo?.dietaryRequirements || "",
                "Aanvullende opmerkingen": (item) => item.importantInfo?.comments || "",
                "Relaties geboekte plekken": (item) => item.invitationTypeAsString === "intern" ? item.noOfInvites : "",
                "Registratie-/wijzigingsdatum": (item) => new Date(item.lastModifiedDate).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                }),
            };
        };
        const eventSpecificFields = eventType === "family" ? familyFields : getDirectorsFields();
        const fields = {
            ...commonFields,
            ...eventSpecificFields,
            //...joinDateFields,
            //...parkingDateFields,
            //...accommodationDateFields,
        };
        // Find the maximum number of adults and children across all items for family events
        let maxAdults = 0;
        let maxChildren = 0;
        let maxExternals = 0;
        if (eventType === "family") {
            items.forEach((item) => {
                const adultsCount = item.adultsInfo?.length || 0;
                const childrenCount = item.childrenInfo?.length || 0;
                maxAdults = Math.max(maxAdults, adultsCount);
                maxChildren = Math.max(maxChildren, childrenCount);
            });
        }
        else {
            // For directors events, find maximum externals
            items.forEach((item) => {
                const externalsCount = item.externals?.length || 0;
                maxExternals = Math.max(maxExternals, externalsCount);
            });
        }
        return items.map((item) => {
            const transformedItem = {};
            // Add common and event-specific fields
            Object.entries(fields).forEach(([key, value]) => {
                transformedItem[key] =
                    typeof value === "function" ? value(item) : item[value];
            });
            // Add adult information for family events
            if (eventType === "family") {
                for (let i = 1; i <= maxAdults; i++) {
                    const adult = item.adultsInfo?.[i - 1];
                    transformedItem[`Volwassene (${i}) Voornaam`] =
                        adult?.firstName || "";
                    transformedItem[`Volwassene (${i}) Tussenvoegsel`] =
                        adult?.middleName || "";
                    transformedItem[`Volwassene (${i}) Achternaam`] =
                        adult?.lastName || "";
                    transformedItem[`Volwassene (${i}) Relatie`] =
                        adult?.relationship || "";
                    transformedItem[`Volwassene (${i}) Geboortedatum`] =
                        adult?.dateOfBirth
                            ? new Date(adult.dateOfBirth).toLocaleDateString()
                            : "";
                    transformedItem[`Volwassene (${i}) Leeftijd`] = adult?.dateOfBirth
                        ? Math.floor((new Date().getTime() - new Date(adult.dateOfBirth).getTime()) /
                            (1000 * 60 * 60 * 24 * 365.25))
                        : "";
                }
                // Add children information for family events
                for (let i = 1; i <= maxChildren; i++) {
                    const child = item.childrenInfo?.[i - 1];
                    transformedItem[`Kind (${i}) Voornaam`] = child?.firstName || "";
                    transformedItem[`Kind (${i}) Tussenvoegsel`] =
                        child?.middleName || "";
                    transformedItem[`Kind (${i}) Achternaam`] = child?.lastName || "";
                    transformedItem[`Kind (${i}) Relatie`] = child?.relationship || "";
                    transformedItem[`Kind (${i}) Geboortedatum`] = child?.dateOfBirth
                        ? new Date(child.dateOfBirth).toLocaleDateString()
                        : "";
                    transformedItem[`Kind (${i}) Leeftijd`] = child?.dateOfBirth
                        ? Math.floor((new Date().getTime() - new Date(child.dateOfBirth).getTime()) /
                            (1000 * 60 * 60 * 24 * 365.25))
                        : "";
                }
            }
            // Add external information for directors events
            if (eventType === "directors") {
                for (let i = 1; i <= maxExternals; i++) {
                    const external = item.externals?.[i - 1];
                    transformedItem[`Relatie (${i}) Voornaam`] =
                        external?.firstName || "";
                    transformedItem[`Relatie (${i}) Tussenvoegsel`] =
                        external?.middleName || "";
                    transformedItem[`Relatie (${i}) Achternaam`] =
                        external?.lastName || "";
                    transformedItem[`Relatie (${i}) E-mailadres`] = external?.email || "";
                    transformedItem[`Relatie (${i}) Bedrijf`] =
                        external?.companyName || "";
                }
            }
            return transformedItem;
        });
    };
    const handleExport = () => {
        const paramsWithExportType = { ...selectedParams, exportType };
        dispatch(ExportToExcelAsync(paramsWithExportType))
            .unwrap()
            .then((result) => {
            const data = result?.items;
            if (!data.length) {
                toast.warning("No data available to export");
                return;
            }
            try {
                const exportData = transformData(data);
                const ws = XLSX.utils.json_to_sheet(exportData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Invitations");
                const now = new Date();
                const dateTime = now
                    .toLocaleString("en-GB", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                    .replace(/[/:]/g, "-")
                    .replace(",", "");
                const exportFileName = `${fileName}-${dateTime}.xlsx`;
                XLSX.writeFile(wb, exportFileName);
                toast.success("Data exported successfully!");
            }
            catch (error) {
                toast.error("Failed to export data");
            }
        });
    };
    return !isMd ? (_jsx(PButton, { startIcon: _jsx(ExcelIcon, {}), color: "primary", variant: "outlined", size: "small", onClick: handleExport, children: "Export to Excel" })) : (_jsx(PIconButton, { size: "small", color: theme.palette.primary.main, onClick: handleExport, children: _jsx(ExcelIcon, {}) }));
};
